import type { DocumentDto, Microsoft365Source, PlanLimitNoticeDto, WorkspaceDto } from '@dbrain/shared';
import { Alert, Button, ConfirmDialog, Dialog, EmptyState, Field, Icons, Menu, MenuItem, MenuSeparator, Select, Skeleton, TextInput } from '@dbrain/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { api, isApiError } from '../api/client.js';
import { uploadDocument, useDocuments, useRefreshDocuments } from '../api/documents.js';
import { useCompanyZone, useDashboard } from '../api/hooks.js';
import { useMicrosoft365Status } from '../api/microsoft365.js';
import { useRefetchDocument, useSourceChecks } from '../api/microsoft365-files.js';
import { DataTable, FilterMenu, tableMinWidth, type Column } from '../components/admin/DataTable.js';
import { formatSize } from '../components/chat/ChatParts.js';
import { DocumentNotes, DocumentPreview, documentSize, KindBadge } from '../components/documents/DocumentParts.js';
import { DocumentSourcePanel, ShareWarning, SourceStatus } from '../components/documents/DocumentSource.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { DocumentLimitDialog, OversizeNotice } from '../components/plan/PlanParts.js';
import { useOrganizationAdmin, usePlan, usePlanUsage, useUploadLimitMb } from '../api/plan.js';
import { ScopeChips, ScopeTag, type LibraryScope } from '../components/library/Library.js';
import { FilePicker } from '../components/microsoft365/files/FilePicker.js';
import { ImportNotice, type ImportSummary } from '../components/microsoft365/files/ImportResult.js';
import {
  addMenuView,
  checkIdsOf,
  needsAcknowledgement,
  PICKER_PARAM,
  pickerSourceOf,
  refetchStep,
  sourceFilterOf,
  sourceState,
  targetOfPlace,
  type RefetchOutcome,
  type RefetchWatch,
} from '../components/microsoft365/files/model.js';
import { SourceLabel } from '../components/microsoft365/files/SourceLabel.js';
import { PageHeader } from '../components/PageHeader.js';
import { useExperience, useMeta } from '../experience.js';
import { formatTokens, humanTime } from '@dbrain/app-core/format';

/**
 * The document library (Phase 12; DOC-001, DOC-002, DOC-006, DOC-007, DOC-008; BelgelerYeni, D-118): a drop
 * area and "Belge yükle ▾" with the place it goes to (Kişisel, a workspace, Herkes, as the person may); Kişisel,
 * a team's (a domain's shared workspace), Herkes, other workspaces and chat attachments in one table,
 * sections in the library pages' order with "Tümü"; for each: where it is, its size, how a chat reads it
 * ("Tamamı okunur" or "Büyük · ilgili bölümlerden yanıtlanır"), its state with progress, when it was added;
 * preview, rename, share, read again, delete. Microsoft 365 part 3 (MsBelgelerKaynak): a "Kaynak" column after
 * "Nerede" and "Kaynak: Tümü ▾"; a copy's states (İçe aktarılıyor, Alınamadı, Yeniden alınıyor…, Kaynakta değişti
 * from the viewer's own check, Kaynakta değişiklik yok, Yeniden alınamadı); the aside's source box and "Yeniden al";
 * sharing a copy to a shared place asks for "Anladım"; "Belge yükle ▾" › "Microsoft 365'ten…" opens the picker.
 */
type Filter = 'all' | 'user' | 'team' | 'organization' | 'workspace' | 'conversation';
const CHIPS: Filter[] = ['all', 'user', 'team', 'organization', 'workspace', 'conversation'];
type SourceFilter = Microsoft365Source | 'upload';
const SOURCE_FILTERS: SourceFilter[] = ['upload', 'onedrive', 'sharepoint', 'outlook'];
type Sort = 'date' | 'dateAsc' | 'name' | 'size';
const SORTS: Sort[] = ['date', 'dateAsc', 'name', 'size'];
const ACCEPT = '.pdf,.docx,.docm,.doc,.odt,.rtf,.xlsx,.xlsm,.xls,.ods,.pptx,.pptm,.ppt,.odp,.txt,.md,.markdown,.csv,.tsv,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.bmp';
/** the library pages' order: each team, Herkes, Kişisel, Çalışma alanları, Sohbet ekleri */
const RANK: Record<LibraryScope, number> = { team: 0, organization: 1, user: 2, workspace: 3, conversation: 4, builtin: 5 };

function placeOf(doc: DocumentDto, workspaces: Map<string, WorkspaceDto>): { scope: LibraryScope; name: string | null } {
  if (doc.scope === 'organization') return { scope: 'organization', name: null };
  if (doc.scope === 'user') return { scope: 'user', name: null };
  if (doc.scope === 'conversation' || doc.scope === 'upload') return { scope: 'conversation', name: null };
  const workspace = doc.workspaceId ? workspaces.get(doc.workspaceId) : undefined;
  return { scope: workspace?.directoryManaged ? 'team' : 'workspace', name: workspace?.name ?? null };
}

const groupKey = (place: { scope: LibraryScope; name: string | null }) => (place.scope === 'team' ? `team:${place.name ?? ''}` : place.scope);

function compare(sort: Sort, locale: string): (a: DocumentDto, b: DocumentDto) => number {
  if (sort === 'name') return (a, b) => a.name.localeCompare(b.name, locale);
  if (sort === 'size') return (a, b) => b.size - a.size;
  if (sort === 'dateAsc') return (a, b) => a.createdAt.localeCompare(b.createdAt);
  return (a, b) => b.createdAt.localeCompare(a.createdAt);
}

/** "Tamamı okunur" or "Büyük · ilgili bölümlerden yanıtlanır", the token estimate small under it (DOC-002) */
function InChat({ doc, onRetry, retrying }: { doc: DocumentDto; onRetry: () => void; retrying: boolean }) {
  const { t, i18n } = useTranslation();
  if (doc.status === 'failed') {
    const reason = t(`documents.errors.${doc.errorCode ?? 'processing_failed'}`, { defaultValue: t('documents.errors.processing_failed') });
    return (
      <span className="doc-chat doc-chat--failed">
        <button type="button" className="info-tip info-tip--text" aria-label={t('documents.why')} aria-describedby={`why-${doc.id}`} data-testid="library-why">
          {t('documents.why')}
          <span id={`why-${doc.id}`} role="tooltip" className="info-tip__text">
            {reason}
          </span>
        </button>
        <span aria-hidden="true"> · </span>
        {doc.canManage ? (
          <button type="button" className="link-button" disabled={retrying} onClick={onRetry} data-testid="library-retry">
            {t('documents.retry')}
          </button>
        ) : null}
      </span>
    );
  }
  if (!doc.estimate) return <span className="muted">{t('documents.chat.pending')}</span>;
  const tokens = formatTokens(doc.estimate.tokens, i18n.language);
  return (
    <span className="doc-chat" data-testid="doc-estimate">
      {doc.estimate.fitsContext ? (
        <span>{t('documents.chat.whole')}</span>
      ) : (
        <span className="doc-chat__large">{t('documents.chat.large')}</span>
      )}
      <span className="doc-chat__tokens">
        {doc.estimate.fitsContext
          ? t('documents.chat.wholeTokens', { tokens })
          : t('documents.chat.largeTokens', { tokens, question: formatTokens(doc.estimate.questionTokens, i18n.language) })}
      </span>
    </span>
  );
}

/** Hazır, İşleniyor %60 with its bar, or Okunamadı */
function State({ doc }: { doc: DocumentDto }) {
  const { t } = useTranslation();
  if (doc.status === 'ready') {
    return (
      <span className="doc-status doc-status--ready" data-testid="doc-state" data-status="ready">
        {t('documents.state.ready')}
      </span>
    );
  }
  if (doc.status === 'failed') {
    return (
      <span className="doc-status doc-status--failed" data-testid="doc-state" data-status="failed">
        {t('documents.state.failedShort')}
      </span>
    );
  }
  const percent = doc.progress && doc.progress.total > 0 ? Math.round((doc.progress.done / doc.progress.total) * 100) : null;
  return (
    <span className="doc-status doc-status--busy" data-testid="doc-state" data-status={doc.status}>
      <span>{doc.status === 'queued' ? t('documents.state.queued') : percent !== null ? t('documents.state.processingAt', { percent }) : t('documents.state.processing')}</span>
      <progress className="doc-status__bar" max={100} value={percent ?? undefined} aria-hidden="true" />
    </span>
  );
}

function RenameDialog({ doc, onClose }: { doc: DocumentDto; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshDocuments();
  const [name, setName] = useState(doc.name);
  const save = useMutation({
    mutationFn: () => api<DocumentDto>('PATCH', `/api/documents/${doc.id}`, { name: name.trim() }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Dialog title={t('documents.rename')} closeLabel={t('common.close')} onClose={onClose} data-testid="library-rename-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <Field label={t('documents.columns.name')}>{({ id }) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="library-rename-input" />}</Field>
        <ErrorAlert error={save.error} />
        <div className="row dialog__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" busy={save.isPending} disabled={!name.trim()} data-testid="library-rename-save">
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ShareDialog({ doc, targets, workspaces, onClose }: { doc: DocumentDto; targets: { value: string; label: string }[]; workspaces: readonly WorkspaceDto[]; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshDocuments();
  const [target, setTarget] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  // part 3: a copy of a Microsoft 365 file going to a team, a workspace or Herkes needs the warning's "Anladım"
  const place = target === '' || target === 'user' ? null : targetOfPlace(target === 'organization' ? 'organization' : 'workspace', target === 'organization' ? null : target, workspaces);
  const warned = Boolean(doc.source && place && needsAcknowledgement(place));
  const save = useMutation({
    mutationFn: () =>
      api<DocumentDto>('PATCH', `/api/documents/${doc.id}`, {
        ...(target === 'user' ? { scope: 'user' } : target === 'organization' ? { scope: 'organization' } : { scope: 'workspace', workspaceId: target }),
        ...(warned && acknowledged ? { acknowledged: true } : {}),
      }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const choices = targets.filter((option) => !(option.value === doc.scope || option.value === doc.workspaceId));
  return (
    <Dialog title={doc.scope === 'conversation' ? t('documents.keep') : t('documents.share')} closeLabel={t('common.close')} onClose={onClose} data-testid="library-share-dialog">
      <Field label={t('documents.shareWith')}>
        {({ id }) => (
          <Select
            id={id}
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              setAcknowledged(false);
            }}
            data-testid="library-share-target"
          >
            <option value="">{t('documents.shareWith')}</option>
            {choices.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {warned && place ? <ShareWarning doc={doc} target={place} acknowledged={acknowledged} onAcknowledge={setAcknowledged} /> : null}
      <ErrorAlert error={save.error} />
      <div className="row dialog__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={!target || (warned && !acknowledged)} busy={save.isPending} onClick={() => save.mutate()} data-testid="library-share-save">
          {t('common.save')}
        </Button>
      </div>
    </Dialog>
  );
}

export function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const meta = useMeta();
  const dashboard = useDashboard();
  const refresh = useRefreshDocuments();
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('date');
  const [q, setQ] = useState('');
  const [target, setTarget] = useState('user');
  const [preview, setPreview] = useState<DocumentDto | null>(null);
  const [dragging, setDragging] = useState(false);
  const [renaming, setRenaming] = useState<DocumentDto | null>(null);
  const [sharing, setSharing] = useState<DocumentDto | null>(null);
  const [deleting, setDeleting] = useState<DocumentDto | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // the list's own width beside the aside (v0.15g): which columns fit without scrolling sideways
  const main = useRef<HTMLElement>(null);
  const [mainWidth, setMainWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const node = main.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setMainWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const documents = useDocuments('all', q);
  const admin = useOrganizationAdmin();
  // Phase 16a (D-266, D-267): the person's plan: its file size and how many documents it keeps (B2cBelgeSiniri)
  const plan = usePlan();
  const planUsage = usePlanUsage({ enabled: plan !== null, refetchMs: 30_000 });
  const uploadMb = useUploadLimitMb();
  const [limitHit, setLimitHit] = useState<{ limit: PlanLimitNoticeDto | null; name: string | null } | null>(null);
  const [oversize, setOversize] = useState<{ name: string; size: number } | null>(null);
  const locale = i18n.language;
  const zone = useCompanyZone();
  const workspaceList = dashboard.data?.workspaces ?? [];
  const workspaces = new Map(workspaceList.map((w) => [w.id, w]));
  // where an upload goes: Kişisel, a workspace (a team's shows as the team's), Herkes for admins
  const targets = [
    { value: 'user', label: t('library.scopes.user') },
    ...workspaceList.map((w) => ({ value: w.id, label: w.directoryManaged ? t('library.tags.teamNamed', { name: w.name }) : t('library.tags.workspaceNamed', { name: w.name }) })),
    ...(admin ? [{ value: 'organization', label: t('library.scopes.organization') }] : []),
  ];
  const targetLabel = targets.find((option) => option.value === target)?.label ?? t('library.scopes.user');
  const add = useMutation({
    mutationFn: async (files: File[]) => {
      const query: Record<string, string> = target === 'user' || target === 'organization' ? { scope: target } : { scope: 'workspace', workspaceId: target };
      const added: DocumentDto[] = [];
      for (const file of files) {
        try {
          added.push(await uploadDocument(file, query));
        } catch (error) {
          // the plan's limits answer with their own windows, not a banner
          if (isApiError(error, 'document_limit')) setLimitHit({ limit: error.limit ?? null, name: file.name });
          if (isApiError(error, 'file_too_large')) setOversize({ name: file.name, size: file.size });
          throw error;
        }
      }
      return added;
    },
    onSuccess: refresh,
    onError: refresh,
  });
  const retry = useMutation({
    mutationFn: (doc: DocumentDto) => api<DocumentDto>('POST', `/api/documents/${doc.id}/retry`),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (doc: DocumentDto) => api('DELETE', `/api/documents/${doc.id}`),
    onSuccess: () => {
      setDeleting(null);
      refresh();
    },
  });

  // part 3: copies of Microsoft 365 files (MsBelgelerKaynak)
  const experience = useExperience();
  const microsoft365 = useMicrosoft365Status();
  const status = microsoft365.data;
  const microsoftMenu = addMenuView(status, experience);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter | null>(null);
  const [picker, setPicker] = useState<{ source: Microsoft365Source; signInError: string | null } | null>(null);
  const [imported, setImported] = useState<ImportSummary | null>(null);
  // a "Yeniden al" started here, followed until the copy is back (then: no change, a new copy, or why not)
  const [watches, setWatches] = useState<Record<string, RefetchWatch>>({});
  const [outcomes, setOutcomes] = useState<Record<string, RefetchOutcome>>({});
  const refetch = useRefetchDocument();
  useEffect(() => {
    const docs = documents.data;
    if (!docs || Object.keys(watches).length === 0) return;
    const next: Record<string, RefetchWatch> = {};
    const done: Record<string, RefetchOutcome> = {};
    let changed = false;
    for (const [id, watch] of Object.entries(watches)) {
      const doc = docs.find((d) => d.id === id);
      if (!doc) {
        changed = true;
        continue;
      }
      const step = refetchStep(watch, doc);
      if (step.outcome) {
        done[id] = step.outcome;
        changed = true;
      } else {
        next[id] = step.watch;
        changed ||= step.watch !== watch;
      }
    }
    if (!changed) return;
    setWatches(next);
    if (Object.keys(done).length > 0) setOutcomes((current) => ({ ...current, ...done }));
  }, [documents.data, watches]);
  const startRefetch = (doc: DocumentDto) => {
    setOutcomes(({ [doc.id]: _dropped, ...rest }) => rest);
    setWatches((current) => ({ ...current, [doc.id]: { processedAt: doc.processedAt, updatedAt: doc.updatedAt, seenBusy: false } }));
    refetch.mutate(doc.id, {
      onError: () => setWatches(({ [doc.id]: _dropped, ...rest }) => rest),
    });
  };
  // back from Microsoft's page with the picker's way back: it opens again on the same source
  const [params, setParams] = useSearchParams();
  const pickerBack = pickerSourceOf(params);
  useEffect(() => {
    if (!pickerBack) return;
    setPicker({ source: pickerBack, signInError: params.get('microsoft365') === 'error' ? params.get('reason') : null });
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of [PICKER_PARAM, 'microsoft365', 'reason']) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [pickerBack, params, setParams]);

  const all = (documents.data ?? []).filter((doc) => sourceFilter === null || sourceFilterOf(doc) === sourceFilter).map((doc) => ({ doc, place: placeOf(doc, workspaces) }));
  const count = (scope: LibraryScope) => all.filter((item) => item.place.scope === scope).length;
  const rows = all
    .filter((item) => filter === 'all' || item.place.scope === filter)
    .sort((a, b) => RANK[a.place.scope] - RANK[b.place.scope] || (a.place.name ?? '').localeCompare(b.place.name ?? '', locale) || compare(sort, locale)(a.doc, b.doc));
  const places = new Map(rows.map((item) => [item.doc.id, item.place]));
  // "Kaynakta değişti": the copies on screen, each checked with the viewer's own Microsoft account (never stored)
  const checks = useSourceChecks(checkIdsOf(rows.map((item) => item.doc), status));
  // the aside follows the document as the list refreshes (its state, its source)
  const previewed = preview ? ((documents.data ?? []).find((d) => d.id === preview.id) ?? preview) : null;
  // the filter only where copies can be: some document has a source, or Microsoft 365 is set up for the firm
  const sourcesShown = (documents.data ?? []).some((doc) => doc.source) || Boolean(status && status.state !== 'unsupported' && status.state !== 'not_configured');
  const groupSizes = new Map<string, number>();
  for (const item of rows) groupSizes.set(groupKey(item.place), (groupSizes.get(groupKey(item.place)) ?? 0) + 1);
  const groupTitle = (key: string) => {
    const size = t('documents.sections.count', { count: groupSizes.get(key) ?? 0 });
    if (key.startsWith('team:')) return `${t('documents.sections.teamTitle', { team: key.slice(5) })} · ${size}`;
    return `${t(`library.scopes.${key}`)} · ${t(`documents.sections.${key}`)} · ${size}`;
  };
  const files = (list: FileList | null) => {
    const picked = [...(list ?? [])];
    if (picked.length === 0) return;
    if (plan) {
      // checked before anything is sent: a file over the size, or no room left for one more document
      const over = picked.find((file) => file.size > uploadMb * 1024 * 1024);
      setOversize(over ? { name: over.name, size: over.size } : null);
      if (over) return;
      const kept = planUsage.data?.documents;
      if (kept && kept.count + 1 > kept.limit) {
        setLimitHit({ limit: { kind: 'documents', plan: plan.id, limit: kept.limit, used: kept.count }, name: picked[0]?.name ?? null });
        return;
      }
    }
    add.mutate(picked);
  };
  const columns: Column<DocumentDto>[] = [
    {
      id: 'name',
      header: t('documents.columns.name'),
      cell: (doc) => (
        <span className="doc-cell">
          <KindBadge kind={doc.kind} />
          <span className="doc-cell__text">
            <button type="button" className="link-button doc-cell__name" onClick={() => setPreview(doc)} disabled={doc.status === 'failed'} title={doc.name} data-testid="library-open">
              {doc.name}
            </button>
            {doc.scope === 'conversation' ? (
              <span className="doc-cell__chat">{doc.conversationTitle ? t('documents.inChat', { title: doc.conversationTitle }) : t('documents.inSomeChat')}</span>
            ) : null}
            <DocumentNotes doc={doc} />
          </span>
        </span>
      ),
    },
    { id: 'where', header: t('documents.columns.where'), width: 'md', cell: (doc) => <ScopeTag {...(places.get(doc.id) ?? { scope: 'user', name: null })} /> },
    { id: 'source', header: t('documents.columns.source'), width: 'md', cell: (doc) => <SourceLabel source={doc.source} /> },
    {
      id: 'size',
      header: t('documents.columns.size'),
      width: 'sm',
      // pages, slides or sheets and the file's size (how much a chat reads is in "Sohbette"); in the narrow
      // column a long one breaks only after the "·"
      cell: (doc) => (
        <span className="doc-size">
          {[doc.kind === 'text' || doc.kind === 'markdown' ? null : documentSize(doc, t), formatSize(doc.size)]
            .filter((part): part is string => Boolean(part))
            .map((part) => part.replace(/ /g, '\u00a0'))
            .join('\u00a0· ') || '—'}
        </span>
      ),
    },
    { id: 'chat', header: t('documents.columns.chat'), width: 'lg', cell: (doc) => <InChat doc={doc} retrying={retry.isPending} onRetry={() => retry.mutate(doc)} /> },
    {
      id: 'state',
      header: t('documents.columns.state'),
      width: 'sm',
      cell: (doc) => {
        const source = sourceState(doc, checks.get(doc.id), outcomes[doc.id]);
        return source ? <SourceStatus state={source} /> : <State doc={doc} />;
      },
    },
    { id: 'added', header: t('documents.columns.added'), width: 'sm', sort: 'added', cell: (doc) => <span className="doc-added">{humanTime(doc.createdAt, locale, t, false, zone)}</span> },
  ];
  // "Kaynak" only where copies can be, as its filter (elsewhere every row would say "Bilgisayardan"). v0.15g: the list
  // shows the columns that fit its own width, so it never scrolls sideways (1280 px with the sidebar open and Microsoft
  // 365 set up needs 1068 px in 918): Sohbette goes first, then Boyut, Eklenme and Nerede (the side panel names them
  // all). Beside the side panel Sohbette and Boyut are gone anyway (MsBelgelerKaynak: Belge, Nerede, Kaynak, Durum,
  // Eklenme), and Durum ("Kaynakta değişti") always stays in sight
  const sourced = columns.filter((column) => sourcesShown || column.id !== 'source');
  const optional = ['chat', 'size', 'added', 'where'];
  const fitting = [0, 1, 2, 3, 4].slice(preview ? 2 : 0).map((drop) => sourced.filter((column) => !optional.slice(0, drop).includes(column.id)));
  const shownColumns = mainWidth === null ? (fitting[0] ?? sourced) : (fitting.find((list) => tableMinWidth(list, { rowMenu: true }) <= mainWidth) ?? fitting[fitting.length - 1] ?? sourced);
  return (
    <div className="page page--wide documents-page">
      <PageHeader
        title={t('documents.title')}
        intro={t('documents.intro')}
        actions={
          <>
          {/* B2cBelgeSiniri: "2 / 2 belge · Ücretsiz" */}
          {plan && planUsage.data ? (
            <span className="plan-count" data-testid="documents-plan-count">
              {t('b2c.documents.count', { count: planUsage.data.documents.count, limit: planUsage.data.documents.limit.toLocaleString(locale), plan: plan.name })}
            </span>
          ) : null}
          <div className="upload-split">
            <input
              ref={input}
              type="file"
              multiple
              accept={ACCEPT}
              className="visually-hidden"
              data-testid="library-file"
              aria-label={t('documents.upload')}
              onChange={(e) => {
                files(e.target.files);
                e.target.value = '';
              }}
            />
            <Button size="lg" icon={<Icons.upload size={18} />} busy={add.isPending} onClick={() => input.current?.click()} data-testid="library-upload">
              {t('documents.upload')}
            </Button>
            <Menu align="right" label={t('documents.uploadTarget')} triggerClassName="ui-button ui-button--primary ui-button--lg upload-split__more" trigger={<Icons.chevronDown size={16} />} data-testid="library-upload-target">
              {targets.map((option) => (
                <MenuItem
                  key={option.value}
                  icon={option.value === target ? <Icons.check /> : <span className="menu-spacer" aria-hidden="true" />}
                  onClick={() => {
                    setTarget(option.value);
                    input.current?.click();
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
              {/* part 3 (no mockup): the same picker as the composer's "+", without "Bu sohbet" */}
              {microsoftMenu && microsoftMenu.sources[0] ? (
                <>
                  <MenuSeparator />
                  <MenuItem icon={<Icons.connections />} onClick={() => setPicker({ source: microsoftMenu.sources[0] as Microsoft365Source, signInError: null })} data-testid="library-from-microsoft">
                    {t('documents.source.fromMicrosoft')}
                  </MenuItem>
                </>
              ) : null}
            </Menu>
          </div>
          </>
        }
      />
      <div
        className={dragging ? 'drop-area drop-area--over' : 'drop-area'}
        onDragOver={(e: DragEvent) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e: DragEvent) => {
          e.preventDefault();
          setDragging(false);
          files(e.dataTransfer.files);
        }}
        data-testid="library-drop"
      >
        <Icons.upload size={16} />
        <span className="drop-area__lead">
          <strong>{t('documents.dropHere')}</strong> {t('documents.dropOr')}{' '}
          <button type="button" className="link-button" onClick={() => input.current?.click()}>
            {t('documents.choose')}
          </button>
        </span>
        <span className="drop-area__meta">
          {t('documents.acceptedShort', { mb: plan ? uploadMb : (meta.data?.uploadMaxMb ?? 100) })} · {t('documents.uploadPlace')}:{' '}
          <span className="chip-select drop-area__target">
            <Select size="sm" value={target} onChange={(e) => setTarget(e.target.value)} aria-label={t('documents.uploadTarget')} title={targetLabel} data-testid="library-upload-scope">
              {targets.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </span>
        </span>
      </div>
      {oversize ? <OversizeNotice size={oversize.size} limitMb={uploadMb} /> : null}
      {plan && planUsage.data && planUsage.data.documents.count > planUsage.data.documents.limit ? (
        <Alert tone="warning" title={t('b2c.documents.over', { plan: plan.name, count: planUsage.data.documents.count, limit: planUsage.data.documents.limit })} data-testid="documents-over-limit" />
      ) : null}
      <ErrorAlert error={isApiError(add.error, 'document_limit') || isApiError(add.error, 'file_too_large') ? null : (add.error ?? documents.error ?? retry.error ?? refetch.error)} />
      {add.isSuccess ? <Alert tone="success" title={t('documents.uploaded', { count: add.data.length })} /> : null}
      {imported ? <ImportNotice summary={imported} onClose={() => setImported(null)} /> : null}
      <div className="library-toolbar">
        <ScopeChips
          label={t('documents.filter')}
          testId="library-filter"
          active={filter}
          onChange={setFilter}
          items={CHIPS.map((key) => (key === 'all' ? { key, label: t('library.scopes.all'), count: all.length } : { key, label: t(`library.scopes.${key}`), count: count(key) }))}
        />
        <div className="library-toolbar__end">
          {sourcesShown ? (
            <FilterMenu
              label={t('documents.source.filter')}
              value={sourceFilter ?? undefined}
              allLabel={t('documents.source.all')}
              options={SOURCE_FILTERS.map((value) => ({ value, label: t(`documents.source.${value}`) }))}
              onChange={(value) => setSourceFilter((value as SourceFilter | null) ?? null)}
              testId="library-source"
            />
          ) : null}
          <span className="chip-select documents-sort">
            <Select size="sm" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label={t('documents.sort.label')} data-testid="library-sort">
              {SORTS.map((key) => (
                <option key={key} value={key}>
                  {t('documents.sort.option', { order: t(`documents.sort.${key}`) })}
                </option>
              ))}
            </Select>
          </span>
          <label className="search-pill">
            <Icons.search size={16} />
            <span className="ui-visually-hidden">{t('documents.search')}</span>
            <input type="search" value={q} placeholder={t('documents.search')} onChange={(e) => setQ(e.target.value)} data-testid="library-search" />
          </label>
        </div>
      </div>
      <div className="documents-layout">
        <section ref={main} className="documents-layout__main">
          {documents.isPending ? <Skeleton block /> : null}
          {documents.data && rows.length === 0 ? (
            <EmptyState icon={<Icons.documents />} title={q ? t('documents.noMatch') : t('documents.empty')} />
          ) : documents.data ? (
            <>
              <DataTable
                label={t('documents.title')}
                columns={shownColumns}
                rows={rows.map((item) => item.doc)}
                rowKey={(doc) => doc.id}
                rowTestId={() => 'library-document'}
                rowData={(doc) => ({ 'data-name': doc.name, 'data-status': doc.status, 'data-source': sourceFilterOf(doc) })}
                rowClassName={(doc) => (preview?.id === doc.id ? 'dt__row--selected' : undefined)}
                sort={{ key: 'added', desc: sort !== 'dateAsc' }}
                onSort={() => setSort(sort === 'dateAsc' ? 'date' : 'dateAsc')}
                {...(filter === 'all' ? { groupOf: (doc: DocumentDto) => groupKey(places.get(doc.id) ?? { scope: 'user', name: null }), groupLabel: groupTitle } : {})}
                rowMenu={(doc) =>
                  doc.canManage || doc.status !== 'failed' ? (
                    <Menu align="right" portal label={t('documents.actions', { name: doc.name })} triggerClassName="dt-row-menu" trigger={<Icons.more size={18} />} data-testid="library-menu">
                      {doc.status !== 'failed' ? (
                        <MenuItem icon={<Icons.preview />} onClick={() => setPreview(doc)}>
                          {t('documents.preview.title')}
                        </MenuItem>
                      ) : null}
                      {doc.canManage ? (
                        <>
                          <MenuItem icon={<Icons.edit />} onClick={() => setRenaming(doc)} data-testid="library-rename">
                            {t('documents.rename')}
                          </MenuItem>
                          <MenuItem icon={<Icons.invite />} onClick={() => setSharing(doc)} data-testid="library-share">
                            {doc.scope === 'conversation' ? t('documents.keep') : t('documents.share')}
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem danger icon={<Icons.trash />} onClick={() => setDeleting(doc)} data-testid="library-delete">
                            {t('common.delete')}
                          </MenuItem>
                        </>
                      ) : null}
                    </Menu>
                  ) : null
                }
                data-testid="library-list"
              />
              <p className="library-foot" data-testid="library-explain">
                {t('documents.explain')}
              </p>
              {plan ? (
                <p className="library-foot" data-testid="documents-plan-footer">
                  {t('b2c.documents.footer', { plan: plan.name, limit: plan.maxDocuments.toLocaleString(locale), mb: uploadMb })}
                </p>
              ) : null}
            </>
          ) : null}
        </section>
        {preview ? (
          <aside className="citation-panel" aria-label={t('documents.preview.title')} data-testid="library-preview">
            <div className="citation-panel__head">
              <p className="citation-panel__doc">
                <KindBadge kind={preview.kind} />
                <strong>{preview.name}</strong>
              </p>
              <Button size="sm" variant="ghost" aria-label={t('common.close')} onClick={() => setPreview(null)}>
                ×
              </Button>
            </div>
            {previewed?.source ? (
              <DocumentSourcePanel
                doc={previewed}
                check={checks.get(previewed.id)}
                outcome={outcomes[previewed.id]}
                status={status}
                workspaces={workspaceList}
                onRefetch={() => startRefetch(previewed)}
                refetching={refetch.isPending && refetch.variables === previewed.id}
              />
            ) : null}
            <DocumentPreview doc={previewed ?? preview} focus={null} />
          </aside>
        ) : null}
      </div>
      {renaming ? <RenameDialog doc={renaming} onClose={() => setRenaming(null)} /> : null}
      {sharing ? <ShareDialog doc={sharing} targets={targets} workspaces={workspaceList} onClose={() => setSharing(null)} /> : null}
      {picker ? (
        <FilePicker
          source={picker.source}
          composer={false}
          returnPath="/documents"
          signInError={picker.signInError}
          onClose={() => setPicker(null)}
          onImported={(summary) => setImported(summary)}
        />
      ) : null}
      {limitHit ? <DocumentLimitDialog limit={limitHit.limit} name={limitHit.name} onClose={() => setLimitHit(null)} /> : null}
      {deleting ? (
        <ConfirmDialog
          title={t('documents.deleteTitle', { name: deleting.name })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate(deleting)}
          onClose={() => setDeleting(null)}
        >
          {t('documents.confirmDelete', { name: deleting.name })}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
